import { html, TemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { RmTopFindingsByImpactPayload } from '@nirvana/shared/dtos/types/widgets/common';
import { getRoutinesTranslator } from '../../../common/translations';
import { DEFAULT_RM_WIDGET_TOP_ROWS } from '@nirvana/shared/constants';
import { WEB_COMPONENT_GZ_RM_TOP_FINDINGS_IMPACT_WIDGET } from '../../../../webcomponents.constants';
import { translateFn } from '../../../../utils/translator.helper';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { Row, RowConfig } from '../../../../ui/gzBasicTable/types/types';
import { ColumnKeys as MisconfigurationsColumnKeys } from '../../../misconfigurationsApp/types';
import { FontSizes } from '../../../../types/common/types';
import { LinkType } from '../../../../ui/gzLinkComponent/types';
import { renderNumber, renderNumberWithTooltip, renderText, renderTitle } from '../../text';
import { fillArray } from '@nirvana/shared/basic-types/array';
import { State } from '../../../common/constants';
import { computeMaxRowsFit, fitItemsToWidget } from '../../resize';

/**
 * @summary Type here the description of this component
 * @tag gz-rm-top-findings-impact-widget
 */
@customElement(WEB_COMPONENT_GZ_RM_TOP_FINDINGS_IMPACT_WIDGET)
export class GzRmTopFindingsImpactWidgetWebComponent extends GzGenericWidgetContent {
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Object, attribute: true })
  payload: RmTopFindingsByImpactPayload = { topFindingsByImpact: { companyId: '', total: 0, items: [] } };

  routinesTranslator = getRoutinesTranslator();

  static get styles() {
    return styles;
  }

  protected viewAll(): void {
    NavigationHelper.navigateToRiskManagement({
      company: { id: this.payload.topFindingsByImpact?.companyId ?? '' },
      filters: [
        {
          [MisconfigurationsColumnKeys.STATE]: { in: [State.ACTIVE] },
        },
        {
          [MisconfigurationsColumnKeys.RISK_SCORE]: { range: { min: 1, max: 100 } },
        },
      ],
      section: 'findings',
      newTab: true,
    });
  }

  protected renderLoader(): TemplateResult {
    return html` <gz-loader class="pt-1" .rows="${computeMaxRowsFit(this.contentHeight)}" columns="1"></gz-loader>`;
  }

  protected renderContent(): TemplateResult {
    const items: Row[] =
      this.payload.topFindingsByImpact?.items?.map(
        ({ indicatorId, affectedDevices, totalAffectedDevices }): RowConfig => ({
          data: { indicatorId },
          cells: [
            renderText(this.routinesTranslator.translateRoutineName(indicatorId)),
            html` <gz-rm-icons-list
              iconSet="entity"
              iconClassMapType="devices"
              .items="${affectedDevices}"
            ></gz-rm-icons-list>`,
            renderNumberWithTooltip(totalAffectedDevices, translateFn('Total affected resources')),
          ],
        })
      ) ?? [];

    const emptyItem = [renderText('...', true), html` <gz-rm-icons-list></gz-rm-icons-list>`, renderNumber('--', true)];
    const fullItems: Row[] = fitItemsToWidget(fillArray(items, DEFAULT_RM_WIDGET_TOP_ROWS, emptyItem), this.contentHeight);

    return html`
      <gz-basic-table
        style="margin-top: calc(var(--spacer)*0.3)"
        rowsClickable
        alignLastColumnRight
        .onClick="${(rowData: { indicatorId: number }) => {
          if (!rowData) {
            return;
          }

          const { indicatorId } = rowData;

          NavigationHelper.navigateToRiskManagement({
            company: { id: this.payload.topFindingsByImpact?.companyId ?? '' },
            section: 'findings',
            filters: [
              {
                [MisconfigurationsColumnKeys.STATE]: { in: [State.ACTIVE] },
              },
              {
                [MisconfigurationsColumnKeys.MISCONFIGURATION_NAME]: {
                  complexEnumFilter: [{ value: indicatorId }],
                },
              }
            ],
            newTab: true,
          });
        }}"
        .data="${fullItems}"
      ></gz-basic-table>

      <gz-link type="${LinkType.QUIET}" fontSize="${FontSizes.PARAGRAPH_11}" .callback=${() => this.viewAll()}>
        ${translateFn('View all')} (${this.payload.topFindingsByImpact?.total ?? 0})
      </gz-link>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rm-top-findings-impact-widget': GzRmTopFindingsImpactWidgetWebComponent;
  }
}
